import { COLUMNAS } from './reclutamientoTablero'
import type { ClaveColumna } from './reclutamientoTablero'

/**
 * Cómo se ve el estatus de un candidato: etiqueta y colores de la pastilla.
 *
 * Los colores no se escriben aquí: salen de `COLUMNAS`, la misma tabla que
 * pinta el panel de reclutamiento. Así la pastilla de una tabla y la de una
 * tarjeta del tablero no pueden discrepar, que es justo lo que pasaba antes —
 * cada pantalla traía su propio mapa y el mismo `seleccionado` se leía
 * «Aprobado» en un lado y «Seleccionado» en el otro, con colores distintos.
 *
 * Vive separado del componente porque un archivo que exporta un componente y
 * además constantes rompe el refresco en caliente, y ese aviso cuenta contra el
 * tope de lint que tumba el deploy.
 */

export interface EstiloDeEstatus {
  label: string
  texto: string
  fondo: string
}

/** En qué columna del tablero cae cada estatus del backend. */
const COLUMNA_DE_ESTATUS: Record<string, ClaveColumna> = {
  pre_registro: 'sin_revision',
  registrado: 'sin_revision',
  pendiente: 'sin_revision',
  en_proceso: 'en_proceso',
  entrevista_rh: 'entrevista_rh',
  pre_seleccionado: 'pre_seleccionado',
  entrevista_solicitante: 'pre_seleccionado',
  seleccionado: 'seleccionado',
}

/**
 * Los dos estatus que el tablero no dibuja.
 *
 * «Sin revisión» no lleva pastilla en el tablero —una tarjeta que nadie ha
 * tocado no anuncia su estado— pero en una tabla la columna no se puede quedar
 * vacía, así que toma los colores del contador de esa misma columna.
 *
 * Descartado y contratado quedan fuera del tablero: el primero usa el par de
 * rojos que ya emplean las acciones destructivas de RH, y el segundo hereda el
 * verde de «Seleccionado», que es el paso del que viene.
 */
const FUERA_DEL_TABLERO: Record<string, EstiloDeEstatus> = {
  contratado: { label: 'Contratado', texto: '#1ACE5C', fondo: '#F4F7F9' },
  rechazado: { label: 'Rechazado', texto: '#DC2626', fondo: '#FDECEC' },
  rechazado_evaluacion_rh: {
    label: 'Rechazado RH',
    texto: '#DC2626',
    fondo: '#FDECEC',
  },
}

export const estiloDeEstatus = (status: string): EstiloDeEstatus => {
  const propio = FUERA_DEL_TABLERO[status]
  if (propio) return propio

  const columna = COLUMNAS.find((c) => c.clave === COLUMNA_DE_ESTATUS[status])
  if (!columna) return { label: status, texto: '#6A6B6C', fondo: '#F4F4F4' }

  const colores = columna.pastilla ?? columna.badge
  return { label: columna.titulo, texto: colores.texto, fondo: colores.fondo }
}
